import React, { useEffect, useState } from "react";
import { Filter, Download, PackageX } from "lucide-react";
import Datepicker from "react-tailwindcss-datepicker";
import { Pagination } from "@mui/material";
import { useAuthStore } from "../../store/useAuthStore";
import orderServices from "../../services/order.service";
import { toast } from "react-toastify";

function UserDownloadList() {
  const { user } = useAuthStore();
  const USER_ID = user?.userDataObj?.user_id;
  const PAGE_SIZE = 10;
  const [dateValue, setDateValue] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, seTotalCount] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadData, setDownloadData] = useState([]);

  const fetchDownloadLsit = async (params) => {
    try {
      setIsLoading(true);
      const downloadResponse = await orderServices.getDownLoadList(params);
      if (downloadResponse.status) {
        setDownloadData(downloadResponse?.data || []);
        seTotalCount(downloadResponse?.totalCount || 0);
        setTotalPage(downloadResponse?.pagination?.totalPages || 0);
      } else {
        setDownloadData([]);
        seTotalCount(0);
        setTotalPage(0);
        toast.error(
          downloadResponse?.message || "Fail to fetch download list",
          { toastId: "errorFetchDownLoadlist" }
        );
      }
    } catch (error) {
      setDownloadData([]);
      seTotalCount(0);
      setTotalPage(0);
      toast.error(error?.message || "Fail to fetch download list", {
        toastId: "errorFetchDownLoadlist",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const applyFilter = async () => {
    fetchDownloadLsit({
      user_id: USER_ID,
      file_type: selectedType || undefined,
      status: selectedStatus || undefined,
      start_date: dateValue.startDate || "",
      end_date: dateValue.endDate || "",
      limit: PAGE_SIZE,
      offset: 1,
    });
    setCurrentPage(1);
  };
  const handleReset = () => {
    setSelectedStatus("");
    setSelectedType("");
    setDateValue({ startDate: null, endDate: null });
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    if (!USER_ID) return;
    applyFilter();
  }, [selectedType, selectedStatus]);

  useEffect(() => {
    if (dateValue.startDate && dateValue.endDate) {
      applyFilter();
    }
  }, [dateValue.startDate, dateValue.endDate]);

  useEffect(() => {
    if (!USER_ID) return;
    fetchDownloadLsit({
      user_id: USER_ID,
      file_type: selectedType || undefined,
      status: selectedStatus || undefined,
      start_date: dateValue.startDate || "",
      end_date: dateValue.endDate || "",
      limit: PAGE_SIZE,
      offset: currentPage,
    });
  }, [currentPage]);

  useEffect(() => {
    if (!USER_ID) return;
    fetchDownloadLsit({
      user_id: USER_ID,
      file_type: selectedType || undefined,
      status: selectedStatus || undefined,
      start_date: dateValue.startDate || "",
      end_date: dateValue.endDate || "",
      limit: PAGE_SIZE,
      offset: currentPage,
    });
  }, []);

  const NoDataFound = () => (
    <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg">
      <PackageX size={48} className="text-gray-400 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        No Download Found
      </h3>
      <p className="text-sm text-gray-800">
        No Download match your current filters or there are no download
        available.
      </p>
    </div>
  );
  return (
    <React.Fragment>
      <div className="bg-gray-50 p-3">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-3">
            <div className="flex gap-4 items-center">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Filter className="h-5 w-5 mr-2" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
            <div className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-lg font-medium items-end">
              Total Downloads: {totalCount}
            </div>
          </div>
          {showFilters && (
            <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Range
                  </label>
                  <Datepicker
                    displayFormat="DD/MM/YYYY"
                    primaryColor="emerald"
                    minDate={new Date("2025-01-01")}
                    value={dateValue}
                    onChange={(newValue) => setDateValue(newValue)}
                    showShortcuts={true}
                    inputClassName="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="SUCCESS">Success</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page
                  </label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">All Pages</option>
                    <option value="ORDER OVERVIEW">Order Overview</option>
                    <option value="ORDER SUMMARY">Order Summary</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleReset}
                  className="px-6 py-2 text-sm font-medium border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64 min-h-full overflow-y-auto mb-2">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      ) : downloadData.length === 0 ? (
        <NoDataFound />
      ) : (
        <div className="min-h-screen bg-gray-50 p-3">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-emerald-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-500 tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-500 tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-500 tracking-wider">
                      File
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-500 tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {downloadData.map((download) => (
                    <tr
                      key={download.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-medium">
                        {download.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 inline-flex text-sm leading-5 font-medium rounded-full ${
                            download.status === "SUCCESS"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {download.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {download.path ? (
                          <a
                            href={download.path}
                            download // Enables download behavior
                            className="text-emerald-600 hover:text-emerald-800 font-medium transition-colors flex items-center"
                          >
                            <Download className="mr-2" /> Download
                          </a>
                        ) : (
                          <span className="text-gray-400">No File</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        {download.creation_date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-center py-4 mb-6">
                <Pagination
                  count={totalPage}
                  page={currentPage}
                  onChange={(event, value) => handlePageChange(value)}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default UserDownloadList;